import React, { useCallback, useEffect, useMemo, useState } from 'react';
import apiService from '../../services/ApiService';
import companionService, { CompanionHealth } from '../../services/CompanionService';
import { useToast } from '../../contexts/ToastContext';

interface SuggestedChange {
    filePath: string;
    symbol?: string;
    change: string;
    rationale: string;
    risk: string;
}

interface IssueAnalysis {
    id: number;
    issueId: number;
    status: 'Pending' | 'Completed' | 'Failed' | 'Approved' | 'Rejected';
    executionMode: 'Api' | 'Companion';
    provider: string;
    model: string;
    summary?: string;
    rootCause?: string;
    confidence?: string;
    riskLevel?: string;
    evidence: string[];
    suggestedChanges: SuggestedChange[];
    validationPlan: string[];
    needsMoreData: boolean;
    missingData: string[];
    errorMessage?: string;
    requestedByEmail?: string;
    reviewedByEmail?: string;
    reviewNote?: string;
    createdAt: string;
    reviewedAt?: string;
}

interface CompanionRun {
    analysisId: number;
    issueId: number;
    agent: string;
    prompt: string;
    healthPort: number;
}

interface CompanionInfo {
    healthPort: number;
    version: string;
    available: boolean;
}

const STATUS_BADGE: Record<string, string> = {
    Pending: 'bg-warning text-dark',
    Completed: 'bg-primary',
    Failed: 'bg-danger',
    Approved: 'bg-success',
    Rejected: 'bg-secondary',
};

const IssueAnalysisPanel: React.FC<{ issueId: number }> = ({ issueId }) => {
    const { showToast } = useToast();
    const [analyses, setAnalyses] = useState<IssueAnalysis[]>([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [mode, setMode] = useState<'api' | 'companion'>('api');
    const [agent, setAgent] = useState('codex');
    const [companionHealth, setCompanionHealth] = useState<CompanionHealth | null>(null);
    const [companionError, setCompanionError] = useState<string | null>(null);
    const [checkingCompanion, setCheckingCompanion] = useState(false);
    const [healthPort, setHealthPort] = useState(4977);
    const [workspace, setWorkspace] = useState(() => localStorage.getItem('easytools.issueAnalyst.workspace') || '');
    const [reviewNotes, setReviewNotes] = useState<Record<number, string>>({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setAnalyses(await apiService.adminGetDiagnosticIssueAnalyses<IssueAnalysis>(issueId));
        } catch (error) {
            showToast('Agent Analyst', error instanceof Error ? error.message : 'Proposte non disponibili', 'danger');
        } finally {
            setLoading(false);
        }
    }, [issueId, showToast]);

    useEffect(() => { void load(); }, [load]);

    const checkCompanion = useCallback(async (): Promise<{ info: CompanionInfo; health: CompanionHealth }> => {
        setCheckingCompanion(true);
        setCompanionError(null);
        try {
            const info = await apiService.getCompanionInfo<CompanionInfo>();
            setHealthPort(info.healthPort);
            const health = await companionService.health(info.healthPort);
            setCompanionHealth(health);
            const firstUsable = health.agents.find(item => item.available && item.supportsReadOnly);
            if (firstUsable && !health.agents.some(item => item.code === agent && item.available && item.supportsReadOnly))
                setAgent(firstUsable.code);
            return { info, health };
        } catch (error) {
            const message = error instanceof Error ? error.message : 'EasyToolsCompanion non raggiungibile.';
            setCompanionHealth(null);
            setCompanionError(message);
            throw error;
        } finally {
            setCheckingCompanion(false);
        }
    }, [agent]);

    useEffect(() => {
        if (mode === 'companion') void checkCompanion().catch(() => undefined);
    }, [checkCompanion, mode]);

    const usableAgents = useMemo(
        () => companionHealth?.agents.filter(item => item.available && item.supportsReadOnly) ?? [],
        [companionHealth],
    );

    const generate = async () => {
        setBusy(true);
        try {
            if (mode === 'api') {
                const result = await apiService.adminGenerateDiagnosticIssueAnalysis<IssueAnalysis>(issueId, 'anthropic');
                if (result.status === 'Failed') throw new Error(result.errorMessage || 'Analisi API non completata.');
            } else {
                const { info, health } = await checkCompanion();
                const selected = health.agents.find(item => item.code === agent);
                if (!selected?.available || !selected.supportsReadOnly)
                    throw new Error('La CLI selezionata non è disponibile con profilo read-only.');

                localStorage.setItem('easytools.issueAnalyst.workspace', workspace.trim());
                const run = await apiService.adminPrepareDiagnosticIssueCompanion<CompanionRun>(issueId, agent);
                try {
                    const output = await companionService.runReadOnly(
                        run.healthPort || info.healthPort,
                        run.agent,
                        run.prompt,
                        workspace,
                    );
                    const result = await apiService.adminCompleteDiagnosticIssueCompanion<IssueAnalysis>(
                        issueId, run.analysisId, output,
                    );
                    if (result.status === 'Failed') throw new Error(result.errorMessage || 'Output CLI non valido.');
                } catch (cliError) {
                    const message = cliError instanceof Error ? cliError.message : 'Esecuzione CLI non completata.';
                    await apiService.adminCompleteDiagnosticIssueCompanion<IssueAnalysis>(
                        issueId, run.analysisId, undefined, message,
                    ).catch(() => undefined);
                    throw cliError;
                }
            }
            showToast('Agent Analyst', 'Proposta generata e pronta per la review.', 'success');
        } catch (error) {
            showToast('Agent Analyst', error instanceof Error ? error.message : 'Analisi non completata', 'danger');
        } finally {
            await load();
            setBusy(false);
        }
    };

    const review = async (analysis: IssueAnalysis, decision: 'Approved' | 'Rejected') => {
        const note = reviewNotes[analysis.id]?.trim();
        if (decision === 'Rejected' && !note) {
            showToast('Review richiesta', 'Inserisci una motivazione prima di rifiutare la proposta.', 'warning');
            return;
        }
        setBusy(true);
        try {
            await apiService.adminReviewDiagnosticIssueAnalysis(
                issueId, analysis.id, decision, note,
            );
            showToast('Review salvata', decision === 'Approved' ? 'Proposta approvata.' : 'Proposta rifiutata.', 'success');
            await load();
        } catch (error) {
            showToast('Review non salvata', error instanceof Error ? error.message : 'Operazione non riuscita', 'danger');
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className="card border-primary-subtle mb-3">
            <div className="card-header bg-primary-subtle d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div>
                    <strong><i className="bi bi-stars me-2" />Agent Analyst</strong>
                    <div className="small text-body-secondary">Diagnosi read-only e proposta sottoposta ad approvazione admin.</div>
                </div>
                <span className="badge text-bg-light border">Nessuna modifica automatica</span>
            </div>
            <div className="card-body">
                <div className="row g-3 align-items-end">
                    <div className="col-12 col-md-3">
                        <label className="form-label small">Modalità di esecuzione</label>
                        <select className="form-select" value={mode} disabled={busy}
                            onChange={event => setMode(event.target.value as 'api' | 'companion')}>
                            <option value="api">API server-side</option>
                            <option value="companion">CLI locale (Companion)</option>
                        </select>
                    </div>

                    {mode === 'api' ? (
                        <div className="col-12 col-md-4">
                            <label className="form-label small">Provider</label>
                            <input className="form-control" value="Anthropic · configurazione server" disabled />
                        </div>
                    ) : (
                        <>
                            <div className="col-12 col-md-3">
                                <label className="form-label small">Agent CLI read-only</label>
                                <select className="form-select" value={agent} disabled={busy || usableAgents.length === 0}
                                    onChange={event => setAgent(event.target.value)}>
                                    {usableAgents.length === 0 && <option value={agent}>Nessuna CLI disponibile</option>}
                                    {usableAgents.map(item => (
                                        <option key={item.code} value={item.code}>{item.displayName} · {item.version || 'versione rilevata'}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-12 col-md">
                                <label className="form-label small">Workspace locale (opzionale)</label>
                                <input className="form-control" value={workspace} disabled={busy}
                                    onChange={event => setWorkspace(event.target.value)}
                                    placeholder="C:\\Work\\EasyTools\\EasyToolsUI" />
                                <div className="form-text">Il percorso resta nel browser ed è passato solo al Companion locale.</div>
                            </div>
                        </>
                    )}

                    <div className="col-12 col-md-auto d-flex gap-2">
                        {mode === 'companion' && (
                            <button className="btn btn-outline-secondary" disabled={busy || checkingCompanion}
                                onClick={() => void checkCompanion().catch(() => undefined)}>
                                <i className="bi bi-activity me-1" />{checkingCompanion ? 'Verifica…' : 'Verifica'}
                            </button>
                        )}
                        <button className="btn btn-primary" disabled={busy || (mode === 'companion' && usableAgents.length === 0)}
                            onClick={() => void generate()}>
                            {busy ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="bi bi-stars me-2" />}
                            Genera proposta
                        </button>
                    </div>
                </div>

                {mode === 'companion' && (
                    <div className={`alert py-2 mt-3 mb-0 ${companionError ? 'alert-warning' : 'alert-success'}`}>
                        {companionError ? (
                            <><i className="bi bi-exclamation-triangle me-2" />{companionError} Porta attesa: {healthPort}.</>
                        ) : (
                            <><i className="bi bi-shield-check me-2" />Companion pronto. Solo le CLI con profilo read-only sono abilitate.</>
                        )}
                    </div>
                )}
            </div>

            <div className="card-footer bg-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Proposte ({analyses.length})</strong>
                    <button className="btn btn-sm btn-link" disabled={loading} onClick={() => void load()}>
                        <i className="bi bi-arrow-clockwise me-1" />Aggiorna
                    </button>
                </div>
                {loading && <div className="text-center text-body-secondary py-3"><span className="spinner-border spinner-border-sm me-2" />Caricamento…</div>}
                {!loading && analyses.length === 0 && (
                    <div className="text-body-secondary small py-2">Nessuna proposta generata per questo problema.</div>
                )}
                <div className="d-flex flex-column gap-3">
                    {analyses.map(analysis => (
                        <article key={analysis.id} className="border rounded p-3">
                            <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                                <span className={`badge ${STATUS_BADGE[analysis.status] || 'bg-secondary'}`}>{analysis.status}</span>
                                <span className="badge text-bg-light border">{analysis.executionMode}</span>
                                <span className="small text-body-secondary">{analysis.provider} · {analysis.model}</span>
                                <span className="small text-body-secondary ms-auto">
                                    #{analysis.id} · {new Date(analysis.createdAt).toLocaleString('it-IT')}
                                </span>
                            </div>

                            {analysis.status === 'Pending' && (
                                <div className="small text-body-secondary"><span className="spinner-border spinner-border-sm me-2" />Analisi in corso…</div>
                            )}
                            {analysis.errorMessage && <div className="alert alert-danger py-2 mb-0">{analysis.errorMessage}</div>}
                            {analysis.summary && (
                                <>
                                    <h6 className="mb-1">Sintesi</h6>
                                    <p>{analysis.summary}</p>
                                    <div className="row g-3">
                                        <div className="col-12 col-lg-7">
                                            <h6 className="mb-1">Causa probabile</h6>
                                            <p className="mb-2">{analysis.rootCause}</p>
                                        </div>
                                        <div className="col-6 col-lg">
                                            <div className="small text-body-secondary">Confidenza</div>
                                            <strong>{analysis.confidence || '—'}</strong>
                                        </div>
                                        <div className="col-6 col-lg">
                                            <div className="small text-body-secondary">Rischio</div>
                                            <strong>{analysis.riskLevel || '—'}</strong>
                                        </div>
                                    </div>

                                    {analysis.suggestedChanges.length > 0 && (
                                        <div className="mt-2">
                                            <h6>Modifiche suggerite</h6>
                                            <div className="d-flex flex-column gap-2">
                                                {analysis.suggestedChanges.map((change, index) => (
                                                    <div key={`${change.filePath}-${index}`} className="bg-body-tertiary border rounded p-2 small">
                                                        <div className="d-flex flex-wrap gap-2">
                                                            <code>{change.filePath || 'File da identificare'}</code>
                                                            {change.symbol && <code>{change.symbol}</code>}
                                                            <span className="badge text-bg-light border ms-auto">rischio {change.risk}</span>
                                                        </div>
                                                        <div className="mt-1">{change.change}</div>
                                                        <div className="text-body-secondary mt-1">{change.rationale}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="row g-3 mt-1">
                                        <div className="col-12 col-lg-6">
                                            <details>
                                                <summary className="small fw-semibold">Evidenze ({analysis.evidence.length})</summary>
                                                <ul className="small mt-2 mb-0">{analysis.evidence.map((item, index) => <li key={index}>{item}</li>)}</ul>
                                            </details>
                                        </div>
                                        <div className="col-12 col-lg-6">
                                            <details>
                                                <summary className="small fw-semibold">Piano di verifica ({analysis.validationPlan.length})</summary>
                                                <ol className="small mt-2 mb-0">{analysis.validationPlan.map((item, index) => <li key={index}>{item}</li>)}</ol>
                                            </details>
                                        </div>
                                    </div>

                                    {analysis.needsMoreData && (
                                        <div className="alert alert-warning py-2 mt-3 mb-0">
                                            <strong>Dati mancanti:</strong> {analysis.missingData.join(' · ') || 'ulteriori evidenze diagnostiche'}
                                        </div>
                                    )}
                                </>
                            )}

                            {analysis.status === 'Completed' && (
                                <div className="border-top mt-3 pt-3">
                                    <label className="form-label small">Nota di review</label>
                                    <textarea className="form-control mb-2" rows={2} disabled={busy}
                                        value={reviewNotes[analysis.id] || ''}
                                        onChange={event => setReviewNotes(current => ({ ...current, [analysis.id]: event.target.value }))}
                                        placeholder="Obbligatoria per il rifiuto; consigliata per l'approvazione" />
                                    <div className="d-flex gap-2 justify-content-end">
                                        <button className="btn btn-outline-danger" disabled={busy}
                                            onClick={() => void review(analysis, 'Rejected')}>Rifiuta</button>
                                        <button className="btn btn-success" disabled={busy}
                                            onClick={() => void review(analysis, 'Approved')}>
                                            <i className="bi bi-check2-circle me-1" />Approva proposta
                                        </button>
                                    </div>
                                </div>
                            )}
                            {(analysis.status === 'Approved' || analysis.status === 'Rejected') && (
                                <div className="small text-body-secondary border-top mt-3 pt-2">
                                    Review di {analysis.reviewedByEmail || 'admin'}
                                    {analysis.reviewedAt && ` · ${new Date(analysis.reviewedAt).toLocaleString('it-IT')}`}
                                    {analysis.reviewNote && <div className="mt-1">{analysis.reviewNote}</div>}
                                </div>
                            )}
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default IssueAnalysisPanel;
